import { Injectable, signal, computed } from '@angular/core';
import { InvestigationFormEntity } from '../../domain/entities/investigation-form.entity';
import { InvestigationFormDetailEntity } from '../../domain/entities/investigation-form-detail.entity';
import { FieldConstraintTypeResponseDto, FieldTypeResponseDto } from '../../infrastructure/dtos/field-constraint-type-response.dto';

@Injectable({ providedIn: 'root' })
export class InvestigationFormState {
  private readonly _investigationForms = signal<InvestigationFormEntity[]>([]);
  private readonly _investigationFormsCount = signal<number>(0);
  private readonly _investigationFormSelectedToEdit = signal<InvestigationFormDetailEntity | null>(null);
  private readonly _fieldTypes = signal<FieldTypeResponseDto[]>([]);
  private readonly _fieldConstraintTypes = signal<FieldConstraintTypeResponseDto[]>([]);
  private readonly _loadingInvestigationForms = signal(false);
  private readonly _loadingInvestigationForm = signal(false);
  private readonly _loadingFieldConstraintTypes = signal(false);
  private readonly _errorInvestigationForms = signal<string | null>(null);
  private readonly _errorInvestigationForm = signal<string | null>(null);
  private readonly _savingInvestigationForm = signal(false);

  public readonly investigationForms = this._investigationForms.asReadonly();
  public readonly investigationFormsCount = this._investigationFormsCount.asReadonly();
  public readonly investigationFormSelectedToEdit = this._investigationFormSelectedToEdit.asReadonly();
  public readonly fieldTypes = this._fieldTypes.asReadonly();
  public readonly fieldConstraintTypes = this._fieldConstraintTypes.asReadonly();
  public readonly loadingInvestigationForms = this._loadingInvestigationForms.asReadonly();
  public readonly loadingInvestigationForm = this._loadingInvestigationForm.asReadonly();
  public readonly loadingFieldConstraintTypes = this._loadingFieldConstraintTypes.asReadonly();
  public readonly errorInvestigationForms = this._errorInvestigationForms.asReadonly();
  public readonly errorInvestigationForm = this._errorInvestigationForm.asReadonly();
  public readonly savingInvestigationForm = this._savingInvestigationForm.asReadonly();

  public readonly hasInvestigationForms = computed(() => this._investigationForms().length > 0);

  public setInvestigationForms(forms: InvestigationFormEntity[]): void {
    this._investigationForms.set(forms);
  }

  public setInvestigationFormsCount(count: number): void {
    this._investigationFormsCount.set(count);
  }

  public setInvestigationFormSelectedToEdit(form: InvestigationFormDetailEntity | null): void {
    this._investigationFormSelectedToEdit.set(form);
  }

  public setLoadingInvestigationForms(loading: boolean): void {
    this._loadingInvestigationForms.set(loading);
  }

  public setLoadingInvestigationForm(loading: boolean): void {
    this._loadingInvestigationForm.set(loading);
  }

  public setErrorInvestigationForms(error: string | null): void {
    this._errorInvestigationForms.set(error);
  }

  public setErrorInvestigationForm(error: string | null): void {
    this._errorInvestigationForm.set(error);
  }

  public clearErrorInvestigationForms(): void {
    this._errorInvestigationForms.set(null);
  }

  public clearErrorInvestigationForm(): void {
    this._errorInvestigationForm.set(null);
  }

  public setSavingInvestigationForm(saving: boolean): void {
    this._savingInvestigationForm.set(saving);
  }

  public setFieldTypes(fieldTypes: FieldTypeResponseDto[]): void {
    this._fieldTypes.set(fieldTypes);
  }

  public setFieldConstraintTypes(constraints: FieldConstraintTypeResponseDto[]): void {
    this._fieldConstraintTypes.set(constraints);
  }

  public setLoadingFieldConstraintTypes(loading: boolean): void {
    this._loadingFieldConstraintTypes.set(loading);
  }

  public removeInvestigationForm(id: string): void {
    this._investigationForms.update((forms) => forms.filter((form) => form.id !== id));
  }

  public clear(): void {
    this._investigationForms.set([]);
    this._investigationFormsCount.set(0);
    this._investigationFormSelectedToEdit.set(null);
    this._fieldConstraintTypes.set([]);
    this._loadingInvestigationForms.set(false);
    this._loadingInvestigationForm.set(false);
    this._loadingFieldConstraintTypes.set(false);
    this._errorInvestigationForms.set(null);
    this._errorInvestigationForm.set(null);
    this._savingInvestigationForm.set(false);
  }
}
